import React, { createContext, useContext, useState, useCallback, ReactNode, useRef, useEffect } from 'react';
import { googleEvent, createNotification } from '../utils/dom';
import shared from '../data/js/shared';
import { trackError } from '@/utils/error-tracking';
import { useRouterContext } from './RouterProvider';
import { useUser } from './UserProvider';
import { useProduct } from './ProductProvider';
import {
  GetChatsSuccessResponseSchema,
  FindChatResponseSchema,
  GetChatResponseSchema,
  SendMessageResponseSchema,
  DeleteChatResponseSchema,
  DeleteMessageResponseSchema,
  BlockUserResponseSchema,
  CheckBlockedResponseSchema,
  ReportUserResponseSchema,
} from '@/schemas/api/chat';
import type { SendMessageResponse, GetChatResponse } from '@/schemas/api/chat';
import { postWithBotChallenge } from '@/utils/bot-challenge-client';
import { getRecaptchaToken } from '@/utils/recaptcha-token';

export interface ChatMessage {
  id: string;
  text?: string;
  message?: string;
  userId?: string;
  senderId?: string;
  createdDate?: string;
  timestamp?: string;
  read?: boolean;
}

export interface ChatCompanion {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  trustLevel?: number;
  photos?: {
    thumbUrl: string;
  };
}

export interface ChatProduct {
  id: string;
  title: string;
  price: number;
  photos?: { url: string; thumbUrl: string }[];
  user?: {
    id: string;
    name: string;
  };
}

export interface Chat {
  id: string;
  listingId: string;
  companion: ChatCompanion;
  lastMessage?: string;
  unreadCount?: number;
  timestamp?: string;
}

type GetChatSuccess = Extract<GetChatResponse, { success: true }>;

export interface ThreadMessages {
  messages: GetChatSuccess['messages'];
  /** The other person, as the server names them. Null when it did not say. */
  companion: GetChatSuccess['companion'] | null;
}

export interface BlockedStatus {
  isBlocked: boolean;
  isBlockedBy: boolean;
}

export interface ChatContextType {
  allChats: Chat[];
  /** True when the last chat-list fetch FAILED, as opposed to finding none. */
  allChatsFailed: boolean;
  /** The server has older chats than the ones loaded so far. */
  allChatsHasMore: boolean;
  currentChat: Chat | null;
  chatId: string | null;
  chatMessages: ChatMessage[];
  chatCompanion: ChatCompanion | null;
  chatProduct: ChatProduct | null;

  getAllChats: () => Promise<Chat[]>;
  getChatData: () => Promise<Chat[]>; // Alias for getAllChats for backward compatibility
  /**
   * Load the next page of chats onto the list. Resolves to the whole list so
   * far; with nothing more to load it resolves to the list as it is.
   */
  loadMoreChats: () => Promise<Chat[]>;
  findChatForListing: (listingId: string | number, buyerId: string | number, sellerId: string | number) => Promise<{ success: boolean; chatId: string | null; error?: string }>;
  getMessages: (chatId?: string) => Promise<GetChatResponse | null>;
  /**
   * Read a thread without opening it. `getMessages` makes the thread the open
   * one (chat id, companion, messages); this only returns them, so a menu on a
   * list row can copy a conversation without swapping the one on screen.
   */
  fetchThreadMessages: (chatId: string) => Promise<ThreadMessages | null>;
  /** Mark a chat read for the signed-in user. Resolves true when the server did. */
  markChatRead: (chatId: string) => Promise<boolean>;
  sendChatMessage: (message: string, listingId?: string, chatId?: string) => Promise<SendMessageResponse | null>;
  deleteChat: (chatId: string, setChatList?: (chats: Chat[]) => void) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  resetChat: () => Promise<void>;
  /**
   * Forget the open thread (its id and messages). The provider is app-wide, so
   * a thread opened in My Chats is still "open" on the next listing page; a
   * chat window there with nothing of its own must not reuse it.
   */
  clearOpenChat: () => void;
  setChatId: (id: string | null) => void;
  setCurrentChat: (chat: Chat | null) => void;
  setChatCompanion: (companion: ChatCompanion | null) => void;
  setChatProduct: (product: ChatProduct | null) => void;
  blockUser: (userId: number) => Promise<{ success: boolean; error?: string }>;
  unblockUser: (userId: number) => Promise<{ success: boolean; error?: string }>;
  checkBlocked: (userId: number) => Promise<BlockedStatus>;
  reportUser: (params: { userId: number; details: string; chatId?: number }) => Promise<{ success: boolean; error?: string }>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// A refused chat fetch used to log only `statusText` -- one row read "Forbidden"
// with no status code, though no chat route answers 403. The Vercel firewall
// does, and it says so in `x-vercel-mitigated`; the status and edge headers are
// what tell our refusal apart from the edge's.
function failedResponse(response: Response, action: string): [Error, { action: string; component: string; extra: Record<string, unknown> }] {
  const header = (name: string) => response.headers?.get(name) ?? null;
  return [
    new Error(`${response.status} ${response.statusText}`.trim()),
    {
      action,
      component: 'ChatMessageProvider',
      extra: {
        status: response.status,
        statusText: response.statusText,
        mitigated: header('x-vercel-mitigated'),
        vercelId: header('x-vercel-id'),
        contentType: header('content-type'),
      },
    },
  ];
}

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatMessageProvider');
  }
  return context;
};

interface ChatMessageProviderProps {
  children: ReactNode;
}

// A chat that moved to the top between two requests can come back on a later
// page too, so later pages are matched by id before they are added.
function appendNew(list: Chat[], more: Chat[]): Chat[] {
  const seen = new Set(list.map((c) => c.id));
  return [...list, ...more.filter((c) => !seen.has(c.id))];
}

export const ChatMessageProvider: React.FC<ChatMessageProviderProps> = ({
  children,
}) => {
  // Use SSG-safe router context
  const { asPath, push } = useRouterContext();
  // Get profile from UserProvider (ChatMessageProvider is nested inside UserProvider)
  const { profile } = useUser();
  // Get productDetail from ProductProvider (ChatMessageProvider is nested inside ProductProvider)
  const { productDetail } = useProduct();

  const [allChats, setAllChats] = useState<Chat[]>([]);
  // Kept apart from an empty list: the chats page used to show a failed fetch
  // as "You Have No Chats", which tells someone with chats that they have none.
  const [allChatsFailed, setAllChatsFailed] = useState(false);
  // GET /api/chat/chats sends 50 chats a page. Only page 1 was ever asked for,
  // so a user with more than 50 conversations could not reach the older ones.
  const [allChatsHasMore, setAllChatsHasMore] = useState(false);
  const allChatsPageRef = useRef(0);
  const loadedChatsRef = useRef<Chat[]>([]);
  const hasMoreRef = useRef(false);
  const [currentChat, setCurrentChat] = useState<Chat | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatCompanion, setChatCompanion] = useState<ChatCompanion | null>(null);
  const [chatProduct, setChatProduct] = useState<ChatProduct | null>(null);
  const inflightChatsRef = useRef<Promise<Chat[]> | null>(null);
  const chatIdRef = useRef(chatId);
  const chatCompanionRef = useRef(chatCompanion);

  // Keep refs in sync with latest state. These refs are only read inside
  // callbacks (getMessages/sendMessage), which run after commit, so syncing
  // in an effect rather than during render preserves behavior.
  useEffect(() => {
    chatIdRef.current = chatId;
    chatCompanionRef.current = chatCompanion;
  }, [chatId, chatCompanion]);

  // One page of GET /api/chat/chats, or null when it failed (already tracked).
  // It touches no state: the callers below decide what a page means.
  const fetchChatsPage = useCallback(async (page: number): Promise<{ chats: Chat[]; hasMore: boolean } | null> => {
    const firstPage = page === 1;
    const url = firstPage ? '/api/chat/chats' : `/api/chat/chats?page=${page}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include session cookies
      });

      if (!response.ok) {
        const [error, context] = failedResponse(response, firstPage ? 'Failed to get chats' : 'Failed to get more chats');
        trackError(error, undefined, context);
        return null;
      }

      const data = await response.json();

      // Validate response with Zod
      const parseResult = GetChatsSuccessResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'getAllChats validation failed', component: 'ChatMessageProvider' });
        return null;
      }

      return { chats: parseResult.data.chats as unknown as Chat[], hasMore: parseResult.data.hasMore };
    } catch (error) {
      trackError(error, undefined, { action: firstPage ? 'Error getting all chats' : 'Error getting more chats', component: 'ChatMessageProvider' });
      return null;
    }
  }, []);

  const commitChats = useCallback((chats: Chat[], pages: number, hasMore: boolean) => {
    loadedChatsRef.current = chats;
    allChatsPageRef.current = pages;
    hasMoreRef.current = hasMore;
    setAllChats(chats);
    setAllChatsHasMore(hasMore);
    setAllChatsFailed(false);
  }, []);

  // A refresh reads again every page the user has loaded, not just page 1.
  // Opening a chat changes the route, and the route change refreshes the list:
  // refreshing page 1 alone cut a list of 100 back to 50, so the older chat
  // just opened dropped out of the list and lost its "New" line. A send or a
  // delete refreshes too.
  const refreshChats = useCallback(async (): Promise<Chat[]> => {
    const pagesLoaded = Math.max(1, allChatsPageRef.current);
    const first = await fetchChatsPage(1);
    if (!first) {
      loadedChatsRef.current = [];
      allChatsPageRef.current = 0;
      hasMoreRef.current = false;
      setAllChats([]);
      setAllChatsHasMore(false);
      setAllChatsFailed(true);
      return [];
    }

    let chats = first.chats;
    let hasMore = first.hasMore;
    let page = 1;
    while (hasMore && page < pagesLoaded) {
      const next = await fetchChatsPage(page + 1);
      if (!next) {
        // Keep the older chats already on screen rather than drop them for a
        // failed re-read; Load More stays on offer as it was.
        chats = appendNew(chats, loadedChatsRef.current);
        page = pagesLoaded;
        hasMore = hasMoreRef.current;
        break;
      }
      chats = appendNew(chats, next.chats);
      hasMore = next.hasMore;
      page += 1;
    }

    commitChats(chats, page, hasMore);
    return chats;
  }, [fetchChatsPage, commitChats]);

  // The next page goes under the list. A failed "more" is not a failed list:
  // the chats already loaded stay.
  const fetchNextChats = useCallback(async (): Promise<Chat[]> => {
    const page = allChatsPageRef.current + 1;
    const next = await fetchChatsPage(page);
    if (!next) return loadedChatsRef.current;
    const chats = appendNew(loadedChatsRef.current, next.chats);
    commitChats(chats, page, next.hasMore);
    return chats;
  }, [fetchChatsPage, commitChats]);

  // A second caller while a fetch is in flight gets THAT fetch, not an empty
  // list. It used to get `[]` straight back, and the chats page rendered that as
  // "You Have No Chats" -- for a seller with chats, whenever a send or a delete
  // happened to be refreshing the list at the same moment.
  const runChatsFetch = useCallback((run: () => Promise<Chat[]>): Promise<Chat[]> => {
    if (inflightChatsRef.current) return inflightChatsRef.current;
    inflightChatsRef.current = run().finally(() => {
      inflightChatsRef.current = null;
    });
    return inflightChatsRef.current;
  }, []);

  const getAllChats = useCallback((): Promise<Chat[]> => runChatsFetch(refreshChats), [runChatsFetch, refreshChats]);

  // Shares the in-flight guard, so a double click on "Load more" asks once.
  const loadMoreChats = useCallback((): Promise<Chat[]> => {
    if (inflightChatsRef.current) return inflightChatsRef.current;
    if (!allChatsHasMore) return Promise.resolve(loadedChatsRef.current);
    return runChatsFetch(fetchNextChats);
  }, [allChatsHasMore, runChatsFetch, fetchNextChats]);


  const findChatForListing = useCallback(async (
    listingId: string | number,
    buyerId: string | number,
    sellerId: string | number
  ): Promise<{ success: boolean; chatId: string | null; error?: string }> => {
    try {
      const params = new URLSearchParams({
        listingId: String(listingId),
        buyerId: String(buyerId),
        sellerId: String(sellerId),
      });
      const response = await fetch(`/api/chats/find?${params}`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();

      // Validate response with Zod
      const parseResult = FindChatResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'findChatForListing validation failed', component: 'ChatMessageProvider' });
        return { success: false, chatId: null, error: 'Invalid response format' };
      }

      if (parseResult.data.success) {
        return { success: true, chatId: parseResult.data.chatId };
      }
      return { success: false, chatId: null, error: (parseResult.data as { error?: string }).error };
    } catch (error) {
      trackError(error, undefined, { action: 'findChatForListing', component: 'ChatMessageProvider' });
      return { success: false, chatId: null, error: 'Failed to find chat' };
    }
  }, []);

  const getMessages = useCallback(async (messageChatId?: string): Promise<GetChatResponse | null> => {
    const currentChatId = messageChatId || chatIdRef.current;
    if (!currentChatId) return null;

    try {
      const response = await fetch(`/api/chat/${currentChatId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include session cookies
      });

      if (!response.ok) {
        // 404 means the chat is gone (deleted, or never was): an old email or
        // notification link. A retry can never work and nothing broke, so say
        // so and log nothing.
        const gone = response.status === 404;
        if (!gone) {
          const [error, context] = failedResponse(response, 'Failed to get messages');
          trackError(error, undefined, context);
        }
        createNotification({
          msg: gone ? 'This conversation was deleted.' : 'Failed to load chat. Please try again.',
          type: gone ? 'info' : 'error',
        });
        if (asPath.includes('account/chats')) {
          push('/account/chats');
        }
        return null;
      }

      const data = await response.json();

      // Validate response with Zod
      const parseResult = GetChatResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'getMessages validation failed', component: 'ChatMessageProvider' });
        return null;
      }

      if (!parseResult.data.success) {
        trackError(new Error((parseResult.data as { error?: string }).error), undefined, { action: 'getMessages returned error', component: 'ChatMessageProvider' });
        return null;
      }

      const validatedData = parseResult.data;
      const result = {
        messages: validatedData.messages,
        item: validatedData.item || null,
        buyer: validatedData.buyer || null,
        seller: validatedData.seller || null,
        companion: validatedData.companion || null,
      };

      // Find the chat from allChats to get companion data
      // Don't call getAllChats() here to avoid infinite loop
      const chatFromList = allChats.find(ac => currentChatId === ac.id);

      setChatId(currentChatId);
      setCurrentChat(chatFromList || null);

      // The server names the other person outright. The chat list and the
      // buyer/seller guesswork below are only for a response without it.
      let companionData = (result.companion as unknown as ChatCompanion | null) || chatFromList?.companion;

      // If we don't have companion from chat list, try other sources
      if (!companionData) {
        // Check if the API response has companion data
        // Use String() to ensure type consistency in ID comparisons
        const profileIdStr = profile?.id ? String(profile.id) : null;

        // If we have buyer and seller from API, determine which is the companion
        if (result?.buyer && result?.seller && profileIdStr) {
          // If logged-in user is NOT the buyer, then buyer is the companion
          if (profileIdStr !== String(result.buyer.id)) {
            companionData = result.buyer as unknown as ChatCompanion;
          }
          // If logged-in user is NOT the seller, then seller is the companion
          else if (profileIdStr !== String(result.seller.id)) {
            companionData = result.seller as unknown as ChatCompanion;
          }
        }
        // Fallback: try to pick one if we don't know profile yet
        else if (!profileIdStr) {
          // If we don't have profile loaded, default to seller as companion (buyer is usually the logged-in user viewing purchases)
          if (result?.seller) {
            companionData = result.seller as unknown as ChatCompanion;
          } else if (result?.buyer) {
            companionData = result.buyer as unknown as ChatCompanion;
          }
        }

        // Additional fallbacks
        if (!companionData && chatCompanionRef.current) {
          // Keep existing companion if we have it
          companionData = chatCompanionRef.current;
        }
        if (!companionData && productDetail?.user && profileIdStr && profileIdStr !== String(productDetail.user.id)) {
          companionData = productDetail.user as unknown as ChatCompanion;
        }
      }

      // Augment companion data if missing firstName/lastName
      if (companionData && (!companionData.firstName || !companionData.lastName)) {
        // Try to get complete data from productDetail or result.item
        const fullUserData = (
          (productDetail?.user?.id === companionData.id && productDetail.user) ||
          (result?.item?.user?.id === companionData.id && result.item.user)
        ) as unknown as ChatCompanion | undefined;

        if (fullUserData && (fullUserData.firstName || fullUserData.lastName)) {
          companionData = {
            ...companionData,
            firstName: companionData.firstName || fullUserData.firstName,
            lastName: companionData.lastName || fullUserData.lastName,
            trustLevel: companionData.trustLevel ?? fullUserData.trustLevel,
          };
        }
      }

      // Set the state similar to production
      setChatCompanion(companionData || null);
      setChatProduct((result?.item || {}) as unknown as ChatProduct);
      setChatMessages(result.messages as unknown as ChatMessage[]);

      // Scroll to last message
      setTimeout(() => {
        const bubbles = document.querySelectorAll('.bubble-spacer');
        const lastBubble = bubbles[bubbles.length - 1] as HTMLElement;
        if (lastBubble) {
          lastBubble.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 1);

      return data;
    } catch (error) {
      trackError(error, undefined, { action: 'Error getting messages', component: 'ChatMessageProvider' });
      return null;
    }
  }, [allChats, profile, productDetail, asPath, push]);

  // Nothing called PUT /api/chat/[id]/read, so a chat never became read: its
  // "New" badge in My Chats and the Chats badge in the account menu stayed up
  // after the conversation had been opened and answered.
  const markChatRead = useCallback(async (readChatId: string): Promise<boolean> => {
    try {
      const response = await fetch(`/api/chat/${readChatId}/read`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (!response.ok) {
        const [error, context] = failedResponse(response, 'Failed to mark chat read');
        trackError(error, undefined, context);
        return false;
      }
      return true;
    } catch (error) {
      trackError(error, undefined, { action: 'Error marking chat read', component: 'ChatMessageProvider' });
      return false;
    }
  }, []);

  // "Copy Whole Thread" on a My Chats row used getMessages, which also makes
  // that thread the open one: copying a row's thread swapped the conversation
  // on screen for it. This reads the thread and sets nothing.
  const fetchThreadMessages = useCallback(async (threadChatId: string): Promise<ThreadMessages | null> => {
    try {
      const response = await fetch(`/api/chat/${threadChatId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const [error, context] = failedResponse(response, 'Failed to get thread');
        trackError(error, undefined, context);
        return null;
      }

      const parseResult = GetChatResponseSchema.safeParse(await response.json());
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'fetchThreadMessages validation failed', component: 'ChatMessageProvider' });
        return null;
      }
      // strict is off, so `success` does not narrow the union: cast, as getMessages does.
      if (!parseResult.data.success) {
        trackError(new Error((parseResult.data as { error?: string }).error), undefined, { action: 'fetchThreadMessages returned error', component: 'ChatMessageProvider' });
        return null;
      }
      const thread = parseResult.data as GetChatSuccess;

      return {
        messages: thread.messages,
        companion: thread.companion ?? null,
      };
    } catch (error) {
      trackError(error, undefined, { action: 'Error getting thread', component: 'ChatMessageProvider' });
      return null;
    }
  }, []);

  const sendChatMessage = useCallback(async (
    message: string,
    listingId?: string,
    messageChatId?: string
  ): Promise<SendMessageResponse | null> => {
    const currentListingId = listingId || productDetail?.id;
    const currentChatId = messageChatId || chatIdRef.current || null;

    if (!currentListingId) {
      trackError(new Error('Cannot send message: No listing ID provided'), undefined, { component: 'ChatMessageProvider' });
      return null;
    }

    // Create optimistic message for immediate UI update
    const tempId = `temp-${Date.now()}`;
    // "YYYY-MM-DD HH:MM:SS" in UTC, the same form /api/chat/[id] sends: the
    // bubble (ShortTime) and the date line read it as UTC. Local time here
    // showed a PDT sender's 10:00 AM message as 3:00 AM until the refetch.
    const now = new Date();
    const utcDate = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')} ${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}:${String(now.getUTCSeconds()).padStart(2, '0')}`;
    const optimisticMessage: ChatMessage = {
      id: tempId,
      text: message,
      userId: profile?.id || '',
      createdDate: utcDate,
    };

    // Add optimistic message to UI immediately
    setChatMessages(prev => [...prev, optimisticMessage]);

    // Scroll to the new message immediately
    setTimeout(() => {
      const messages = document.querySelectorAll('.msg-cont');
      const lastMessage = messages[messages.length - 1] as HTMLElement;
      if (lastMessage) {
        lastMessage.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 1);

    try {
      // The route scores every message; a low score comes back as a checkbox,
      // which postWithBotChallenge shows before sending again.
      const response = await postWithBotChallenge(
        '/api/chat/send',
        {
          message,
          listingId: Number(currentListingId),
          chatId: currentChatId ? Number(currentChatId) : null,
          recaptchaToken: await getRecaptchaToken('chat_send'),
        },
        { credentials: 'include' } // Include session cookies
      );

      const rawData = await response.json();

      // Validate response with Zod
      const parseResult = SendMessageResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'sendChatMessage validation failed', component: 'ChatMessageProvider' });
        setChatMessages(prev => prev.filter(m => m.id !== tempId));
        return null;
      }

      const data = parseResult.data;

      // Handle owner trying to message own listing
      if (response.status === 400 && !data.success && (data as { code?: string }).code === 'OWNER_CANNOT_MESSAGE') {
        // Remove optimistic message on error
        setChatMessages(prev => prev.filter(m => m.id !== tempId));
        createNotification({
          type: 'error',
          msg: shared.notices.youAreSellerNotice
        });
        googleEvent('try_chat_on_own_listing', { ...productDetail });
        return null;
      }

      // Handle moderation errors with specific messages
      if (!data.success && (data as { code?: string }).code === 'MESSAGE_MODERATION_FAILED') {
        // Remove optimistic message on error
        setChatMessages(prev => prev.filter(m => m.id !== tempId));
        createNotification({
          type: 'error',
          msg: (data as { error?: string }).error || 'Your message could not be sent due to content policy.'
        });
        return null;
      }

      // Handle other errors
      if (!data.success) {
        // Remove optimistic message on error
        setChatMessages(prev => prev.filter(m => m.id !== tempId));

        // Show appropriate message based on error code
        // A block, and a moderation check that could not run, come with a
        // message written for the person typing. The second used to be
        // replaced by the generic one, which hid that nothing was wrong with
        // the message itself.
        let errorMsg = 'Failed to send message. Please try again.';
        const code = (data as { code?: string }).code;
        if (code === 'USER_BLOCKED') {
          errorMsg = (data as { error?: string }).error || 'Unable to send message to this user.';
        } else if (code === 'MESSAGE_MODERATION_UNAVAILABLE' || code === 'RECAPTCHA_CHALLENGE' || code === 'RECAPTCHA_REFUSED') {
          errorMsg = (data as { error?: string }).error || errorMsg;
        }

        createNotification({
          type: 'error',
          msg: errorMsg
        });
        trackError(new Error((data as { error?: string }).error), undefined, { action: 'Chat error', component: 'ChatMessageProvider' });
        return null;
      }

      if (data.chatId) {
        googleEvent('send_chat_message', {
          user: profile,
          listing: productDetail,
        });

        const returnedChatId = data.chatId;

        // If this was a new chat, refresh the chat list first before getting messages
        if (data.isNewChat) {
          await getAllChats();
        }

        // Inside `if (data.chatId)`, so the id is always there.
        // Refresh messages from server to get real IDs and sync state
        await getMessages(String(returnedChatId));
        setChatId(String(returnedChatId));

        return data;
      }

      return null;
    } catch (error) {
      // Remove optimistic message on error
      setChatMessages(prev => prev.filter(m => m.id !== tempId));
      trackError(error, undefined, { action: 'Error sending chat message', component: 'ChatMessageProvider' });
      return null;
    }
  }, [productDetail, profile, getAllChats, getMessages]);

  const deleteChat = useCallback(async (deleteChatId: string, setChatList?: (chats: Chat[]) => void) => {
    try {
      const response = await fetch(`/api/chat/${deleteChatId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include session cookies
      });

      const data = await response.json();

      // Validate response with Zod
      const parseResult = DeleteChatResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'deleteChat validation failed', component: 'ChatMessageProvider' });
        createNotification({
          msg: 'Failed to delete chat. Please try again.',
          type: 'error',
        });
        return;
      }

      if (parseResult.data.success) {
        const chats = await getAllChats();
        if (setChatList) setChatList(chats);

        // Handle navigation
        if (asPath.includes('account/chats')) {
          push('/account/chats');
        }
      } else {
        createNotification({
          msg: (parseResult.data as { error?: string }).error || 'Failed to delete chat. Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      trackError(error, undefined, { action: 'Error deleting chat', component: 'ChatMessageProvider' });
      createNotification({
        msg: 'Failed to delete chat. Please try again.',
        type: 'error',
      });
    }
  }, [getAllChats, asPath, push]);

  const deleteMessage = useCallback(async (messageId: string) => {
    if (!chatId) return;

    try {
      const response = await fetch(`/api/chat/${chatId}/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include session cookies
      });

      const data = await response.json();

      // Validate response with Zod
      const parseResult = DeleteMessageResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'deleteMessage validation failed', component: 'ChatMessageProvider' });
        createNotification({
          msg: 'Failed to delete message. Please try again.',
          type: 'error',
        });
        return;
      }

      if (parseResult.data.success) {
        await getMessages();
      } else {
        createNotification({
          msg: (parseResult.data as { error?: string }).error || 'Failed to delete message. Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      trackError(error, undefined, { action: 'Error deleting message', component: 'ChatMessageProvider' });
      createNotification({
        msg: 'Failed to delete message. Please try again.',
        type: 'error',
      });
    }
  }, [getMessages, chatId]);

  const resetChat = useCallback(async () => {
    await getAllChats();
  }, [getAllChats]);

  const clearOpenChat = useCallback(() => {
    // The ref too, at once: sendChatMessage falls back to it, and the effect
    // that syncs it only runs after the next render.
    chatIdRef.current = null;
    setChatId(null);
    setCurrentChat(null);
    setChatMessages([]);
  }, []);

  const blockUser = useCallback(async (userId: number): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/chat/block', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();

      // Validate response with Zod
      const parseResult = BlockUserResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'blockUser validation failed', component: 'ChatMessageProvider' });
        return { success: false, error: 'Invalid response format' };
      }

      if (parseResult.data.success) {
        return { success: true };
      }
      return { success: false, error: (parseResult.data as { error?: string }).error };
    } catch (error) {
      trackError(error, undefined, { action: 'Error blocking user', component: 'ChatMessageProvider' });
      return { success: false, error: 'Failed to block user' };
    }
  }, []);

  const unblockUser = useCallback(async (userId: number): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/chat/block', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();

      // Validate response with Zod
      const parseResult = BlockUserResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'unblockUser validation failed', component: 'ChatMessageProvider' });
        return { success: false, error: 'Invalid response format' };
      }

      if (parseResult.data.success) {
        return { success: true };
      }
      return { success: false, error: (parseResult.data as { error?: string }).error };
    } catch (error) {
      trackError(error, undefined, { action: 'Error unblocking user', component: 'ChatMessageProvider' });
      return { success: false, error: 'Failed to unblock user' };
    }
  }, []);

  const checkBlocked = useCallback(async (userId: number): Promise<BlockedStatus> => {
    try {
      const response = await fetch(`/api/chat/block?userId=${userId}`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();

      // Validate response with Zod
      const parseResult = CheckBlockedResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'checkBlocked validation failed', component: 'ChatMessageProvider' });
        return { isBlocked: false, isBlockedBy: false };
      }

      return { isBlocked: parseResult.data.isBlocked, isBlockedBy: parseResult.data.isBlockedBy };
    } catch (error) {
      trackError(error, undefined, { action: 'Error checking blocked status', component: 'ChatMessageProvider' });
      return { isBlocked: false, isBlockedBy: false };
    }
  }, []);

  const reportUser = useCallback(async (params: { userId: number; details: string; chatId?: number }): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/chat/report-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      const data = await response.json();

      // Validate response with Zod
      const parseResult = ReportUserResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(parseResult.error, undefined, { action: 'reportUser validation failed', component: 'ChatMessageProvider' });
        return { success: false, error: 'Invalid response format' };
      }

      if (parseResult.data.success) {
        return { success: true };
      }
      return { success: false, error: (parseResult.data as { error?: string }).error };
    } catch (error) {
      trackError(error, undefined, { action: 'Error reporting user', component: 'ChatMessageProvider' });
      return { success: false, error: 'Failed to report user' };
    }
  }, []);

  const value: ChatContextType = {
    allChats,
    allChatsFailed,
    allChatsHasMore,
    currentChat,
    chatId,
    chatMessages,
    chatCompanion,
    chatProduct,
    getAllChats,
    getChatData: getAllChats, // Alias for backward compatibility
    loadMoreChats,
    findChatForListing,
    getMessages,
    fetchThreadMessages,
    markChatRead,
    sendChatMessage,
    deleteChat,
    deleteMessage,
    resetChat,
    clearOpenChat,
    setChatId,
    setCurrentChat,
    setChatCompanion,
    setChatProduct,
    blockUser,
    unblockUser,
    checkBlocked,
    reportUser,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatMessageProvider;